"use client";

import React, { 
  createContext, 
  useContext, 
  useCallback, 
  useMemo,
  ReactNode 
} from 'react';
import { 
  LeaveReviewFormData, 
  createInitialFormData
} from '@/lib/types/leave-review';

const SESSION_STORAGE_KEY = 'leave_review_session';
const SESSION_EXPIRY_HOURS = 24;

const isSessionStorageAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const testKey = '__session_test__';
    window.sessionStorage.setItem(testKey, 'test');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

interface SessionData {
  formData: LeaveReviewFormData;
  navigationState: {
    currentStep: string;
    completedSteps: string[];
  };
  timestamp: number;
}

interface LeaveReviewSessionContextType {
  saveSession: (data: SessionData) => boolean;
  loadSession: () => SessionData | null;
  clearSession: () => boolean;
  hasStoredSession: () => boolean;
  isSessionExpired: (sessionData: SessionData) => boolean;
}

const LeaveReviewSessionContext = createContext<LeaveReviewSessionContextType>({
  saveSession: () => false,
  loadSession: () => null,
  clearSession: () => false,
  hasStoredSession: () => false,
  isSessionExpired: () => true,
});

interface LeaveReviewSessionProviderProps {
  children: ReactNode;
}

export function LeaveReviewSessionProvider({ children }: LeaveReviewSessionProviderProps) {
  const saveSession = useCallback((data: SessionData): boolean => {
    if (!isSessionStorageAvailable()) return false;
    
    try {
      const sessionPayload = {
        ...data,
        timestamp: Date.now(),
      };
      
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionPayload));
      return true;
    } catch (error) {
      console.error('[LeaveReviewSession] Failed to save session:', error);
      return false;
    }
  }, []);

  const loadSession = useCallback((): SessionData | null => {
    if (!isSessionStorageAvailable()) return null;
    
    try {
      const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!stored) return null;
      
      const sessionData = JSON.parse(stored) as SessionData;
      
      if (!sessionData.formData || !sessionData.navigationState || !sessionData.timestamp) {
        clearSession();
        return null;
      }
      
      if (isSessionExpired(sessionData)) {
        clearSession();
        return null;
      }
      
      return sessionData;
    } catch (error) {
      console.error('[LeaveReviewSession] Failed to load session:', error);
      clearSession();
      return null;
    }
  }, []);

  const clearSession = useCallback((): boolean => {
    if (!isSessionStorageAvailable()) return false;
    
    try {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return true;
    } catch {
      return false;
    }
  }, []);

  const hasStoredSession = useCallback((): boolean => {
    if (!isSessionStorageAvailable()) return false;
    
    const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    return !!stored;
  }, []);

  const isSessionExpired = useCallback((sessionData: SessionData): boolean => {
    const currentTime = Date.now();
    const expiryTime = sessionData.timestamp + (SESSION_EXPIRY_HOURS * 60 * 60 * 1000);
    return currentTime > expiryTime;
  }, []);

  const contextValue = useMemo(() => ({
    saveSession,
    loadSession,
    clearSession,
    hasStoredSession,
    isSessionExpired,
  }), [saveSession, loadSession, clearSession, hasStoredSession, isSessionExpired]);

  return (
    <LeaveReviewSessionContext.Provider value={contextValue}>
      {children}
    </LeaveReviewSessionContext.Provider>
  );
}

export function useLeaveReviewSession(): LeaveReviewSessionContextType {
  const context = useContext(LeaveReviewSessionContext);
  if (!context) {
    // Return safe defaults when called outside provider (e.g., landing page)
    return {
      saveSession: () => false,
      loadSession: () => null,
      clearSession: () => false,
      hasStoredSession: () => false,
      isSessionExpired: () => true,
    };
  }
  return context;
} 